<script>
export default {
  name: "proofCreatedV2",
  props: {
    proof: {
      required: true,
      Type: Object,
    },
  },
  data() {
    return {
      proofReviewLinkType: "approver-link",
      copied: false,
      copyResetTask: null,
    };
  },
  computed: {
    reviewLinkId: function () {
      if (this.proof.reviewerPublicId && this.proofReviewLinkType === "reviewer-link") {
        return this.proof.reviewerPublicId;
      }
      return this.proof.publicId;
    },
    reviewLink: function () {
      if ($A.Env.envName === "local") {
        return `http://localhost:8080/review/${this.reviewLinkId}`;
      }
      if ($A.Auth.account.whiteLabelDomainName !== null) {
        return `https://${$A.Auth.account.whiteLabelDomainName}/review/${this.reviewLinkId}`;
      }
      return `https://${window.location.hostname}/review/${this.reviewLinkId}`;
    },
  },
  methods: {
    onShow() {
      this.proofReviewLinkType = "approver-link";
      this.copied = false;
    },
    copyLink() {
      this.$A.CopyStringToClipboard(this.reviewLink);
      this.copied = true;
      if (this.copyResetTask) clearTimeout(this.copyResetTask);
      this.copyResetTask = setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
    addWorkflow() {
      this.$A.NavigateToView("/proof-details", { proof: this.proof, selectedWorkflow: true });
    },
    viewProof() {
      this.$A.NavigateToView("/proof-details", { proof: this.proof });
    },
  },
};
</script>

<template>
  <b-modal
    id="proofCreated"
    size="md"
    centered
    no-stacking
    no-close-on-backdrop
    hide-header
    hide-footer
    body-class="pcv2-body"
    content-class="pcv2-content"
    @show="onShow"
  >
    <!-- Success hero -->
    <div class="pcv2-hero">
      <div class="pcv2-check" aria-hidden="true">
        <svg viewBox="0 0 52 52" width="52" height="52">
          <circle class="pcv2-check-circle" cx="26" cy="26" r="24" fill="none" />
          <path class="pcv2-check-mark" fill="none" d="M15 27l7.5 7.5L37.5 19" />
        </svg>
      </div>
      <h3 class="pcv2-title">Your proof is ready</h3>
      <p class="pcv2-subtitle">
        Share the review link with your contacts, or add an approval workflow first.
      </p>
    </div>

    <div class="pcv2-main">
      <!-- Link type -->
      <div class="pcv2-choice" role="radiogroup">
        <label class="pcv2-card" :class="{ 'is-selected': proofReviewLinkType === 'approver-link' }">
          <input
            type="radio"
            id="approver-proof-link"
            value="approver-link"
            v-model="proofReviewLinkType"
          />
          <span class="pcv2-card-head">
            <span class="pcv2-card-radio" aria-hidden="true"></span>
            <span class="pcv2-card-title">Approver link</span>
          </span>
          <span class="pcv2-card-sub">Approvers can sign off and receive automatic reminders.</span>
        </label>
        <label class="pcv2-card" :class="{ 'is-selected': proofReviewLinkType === 'reviewer-link' }">
          <input
            type="radio"
            id="non-approver-proof-link"
            value="reviewer-link"
            v-model="proofReviewLinkType"
          />
          <span class="pcv2-card-head">
            <span class="pcv2-card-radio" aria-hidden="true"></span>
            <span class="pcv2-card-title">Comment-only link</span>
          </span>
          <span class="pcv2-card-sub">Commenters can view and leave comments — no decisions.</span>
        </label>
      </div>

      <!-- Link + copy -->
      <div class="pcv2-linkrow" :class="{ 'is-copied': copied }">
        <span class="pcv2-link" id="proof_copy_link" :title="reviewLink">{{ reviewLink }}</span>
        <button type="button" class="pcv2-copy" @click="copyLink">
          <svg v-if="!copied" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {{ copied ? "Copied" : "Copy" }}
        </button>
      </div>

      <!-- Actions -->
      <div class="pcv2-actions">
        <button type="button" class="pcv2-ghost-btn" @click="addWorkflow">Add a workflow</button>
        <button type="button" class="pcv2-primary-btn" @click="viewProof">
          View proof
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
    </div>
  </b-modal>
</template>

<style scoped>
::v-deep .modal-dialog {
  max-width: 680px;
}
::v-deep .pcv2-content {
  border: 0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 24px 64px -16px rgba(8, 11, 13, 0.35);
}
::v-deep .pcv2-body {
  padding: 0;
}

/* ============== HERO ============== */
.pcv2-hero {
  position: relative;
  text-align: center;
  padding: 32px 32px 24px;
  background:
    radial-gradient(ellipse 320px 160px at 50% -40px, rgba(1, 146, 86, 0.12), transparent),
    #fbfcfd;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.pcv2-check {
  display: inline-flex;
  margin-bottom: 14px;
  color: #019256;
}
.pcv2-check-circle {
  stroke: #019256;
  stroke-width: 2.5;
  stroke-dasharray: 151;
  stroke-dashoffset: 151;
  animation: pcv2-draw 600ms cubic-bezier(0.65, 0, 0.45, 1) 100ms forwards;
}
.pcv2-check-mark {
  stroke: #019256;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 36;
  stroke-dashoffset: 36;
  animation: pcv2-draw 380ms cubic-bezier(0.65, 0, 0.45, 1) 580ms forwards;
}
@keyframes pcv2-draw {
  to { stroke-dashoffset: 0; }
}

.pcv2-title {
  margin: 0 0 6px;
  font-size: 19px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #111827;
}
.pcv2-subtitle {
  margin: 0 auto;
  max-width: 360px;
  font-size: 13.5px;
  line-height: 1.55;
  color: #6b7280;
}

/* ============== MAIN ============== */
.pcv2-main {
  padding: 20px 24px 24px;
}

.pcv2-choice {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.pcv2-card {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease;
}
.pcv2-card:hover { border-color: rgba(1, 146, 86, 0.45); }
.pcv2-card.is-selected {
  border-color: #019256;
  background: rgba(1, 146, 86, 0.035);
  box-shadow: 0 0 0 1px #019256 inset;
}

.pcv2-card input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.pcv2-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pcv2-card-radio {
  flex-shrink: 0;
  width: 15px;
  height: 15px;
  border-radius: 999px;
  border: 1.5px solid #c9cfd8;
  background: #fff;
  transition: border-color 140ms ease, box-shadow 140ms ease;
}
.pcv2-card.is-selected .pcv2-card-radio {
  border-color: #019256;
  box-shadow: inset 0 0 0 4px #019256;
}
.pcv2-card-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #1f2937;
}
.pcv2-card-sub {
  font-size: 12.5px;
  line-height: 1.5;
  color: #575759;
  padding-left: 23px;
}

@media (max-width: 575px) {
  .pcv2-choice { flex-direction: column; }
}

/* ============== LINK ROW ============== */
.pcv2-linkrow {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 6px 6px 14px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 10px;
  background: #f7f8fa;
  transition: border-color 160ms ease, background-color 160ms ease;
}
.pcv2-linkrow.is-copied {
  border-color: rgba(1, 146, 86, 0.5);
  background: rgba(1, 146, 86, 0.05);
}

.pcv2-link {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pcv2-copy {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 8px 14px;
  border: 0;
  border-radius: 7px;
  background: #019256;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  min-width: 88px;
  justify-content: center;
  transition: background-color 140ms ease;
}
.pcv2-copy:hover { background: #017a48; }

/* ============== ACTIONS ============== */
.pcv2-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
}

.pcv2-ghost-btn {
  padding: 9px 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  background: #fff;
  font-size: 13.5px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.05);
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}
.pcv2-ghost-btn:hover {
  border-color: rgba(1, 146, 86, 0.5);
  color: #017a48;
}

.pcv2-primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border: 0;
  border-radius: 8px;
  background: #019256;
  color: #fff;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(1, 146, 86, 0.25);
  transition: background-color 140ms ease;
}
.pcv2-primary-btn:hover { background: #017a48; }
</style>
